import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Waves, Heart, HeartHandshake, Flower2, Compass, Check, X, Circle, Volume2, VolumeX, Feather } from "lucide-react";
import SoulOrb from "@/components/SoulOrb";
import { endpoints } from "@/lib/api";
import {
  INTENTION_CATEGORIES, getTodaysIntention, setTodaysIntention, clearTodaysIntention,
} from "@/lib/dailyIntention";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { EDITORIAL } from "@/lib/editorialImages";
import { setCarryingIntention } from "@/lib/carryingIntention";

/**
 * DailyIntentionSelector — the Mind → Daily Intentions tab.
 *
 * A calm one-tap picker that teaches the practice (CHOOSE → ANCHOR →
 * ACT) before entering the library, bridges to action through a single
 * editorial image, and lands on a personal practice centerpiece
 * (AN INTENTION FOR TODAY → TODAY'S INTENTION once a selection is made).
 *
 * The underlying selection logic (`getTodaysIntention`, `setTodaysIntention`,
 * `clearTodaysIntention`) is the single source of truth; the Home page's
 * `TodaysIntentionCard` subscribes to the same store so Home mirrors
 * whatever is picked here.
 */

const ICONS = {
  "peace":         Waves,
  "self-love":     Heart,
  "abundance":     Flower2,
  "alignment":     Compass,
  "aligned-love":  HeartHandshake,
};

export default function DailyIntentionSelector() {
  const [selected, setSelected] = useState(() => getTodaysIntention());
  const [pulseSeed, setPulseSeed] = useState(0);
  const [rituals, setRituals] = useState([]);
  const [done, setDone] = useState(new Set());
  const [busy, setBusy] = useState(null);
  // Which intention line is currently reading aloud, keyed as
  // `${cat.slug}-${index}`. At most one line reads at a time so the
  // page stays a quiet listening space.
  const [speakingKey, setSpeakingKey] = useState(null);
  const speechAvailable = canSpeak();
  const navigate = useNavigate();

  useEffect(() => {
    const onChange = () => setSelected(getTodaysIntention());
    window.addEventListener("daily-intention-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("daily-intention-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  // Always stop any active narration when this surface is torn down or
  // the member navigates away — no runaway audio.
  useEffect(() => () => stopSpeaking(), []);

  const toggleRead = (key, line) => {
    if (speakingKey === key) {
      stopSpeaking();
      setSpeakingKey(null);
      return;
    }
    if (!speechAvailable) {
      toast.error("Read aloud isn't available on this browser.");
      return;
    }
    stopSpeaking();
    const ok = speak(line, { rate: 0.85, onEnd: () => setSpeakingKey(null) });
    if (ok) setSpeakingKey(key);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rs, ts] = await Promise.all([endpoints.rituals(), endpoints.completionsToday()]);
        if (!cancelled) {
          setRituals(rs.data || []);
          setDone(new Set((ts.data?.ritual_ids || []).map(String)));
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const pick = (cat, line) => {
    const p = setTodaysIntention({ text: line, category: cat.slug, categoryTitle: cat.title });
    setSelected(p);
    setPulseSeed((s) => s + 1);           // orb glow + 432Hz tone
    toast.success("Set for today.");
  };

  const release = () => {
    clearTodaysIntention();
    setSelected(null);
    toast("Released.");
  };

  const toggleRitual = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await endpoints.completeRitual(r.id);
      setDone((prev) => {
        const n = new Set(prev);
        if (data.completed) n.add(String(r.id));
        else n.delete(String(r.id));
        return n;
      });
      if (data.completed) toast.success("Honored.");
    } catch { toast.error("Could not update."); }
    finally { setBusy(null); }
  };

  // Only surface incomplete Body rituals — the ones that could still be
  // woven into today. Non-Body rituals stay in their own tab.
  const incomplete = useMemo(
    () => rituals.filter((r) => (r.category || "").toLowerCase() === "body" && !done.has(String(r.id))),
    [rituals, done],
  );

  // A flat pool of every library line so the lower "An intention for
  // today" moment can quietly rotate through the whole library when the
  // member has not yet made a selection.
  const allLines = useMemo(() => {
    const out = [];
    for (const cat of INTENTION_CATEGORIES) {
      for (const line of cat.intentions) {
        out.push({ text: line, category: cat.slug, categoryTitle: cat.title });
      }
    }
    return out;
  }, []);
  const [suggestionIdx, setSuggestionIdx] = useState(() =>
    allLines.length ? Math.floor(Math.random() * allLines.length) : 0,
  );
  const [rolling, setRolling] = useState(false);
  const suggestion = allLines[suggestionIdx] || null;
  const rollAnother = () => {
    if (allLines.length <= 1) return;
    setRolling(true);
    let next = suggestionIdx;
    while (next === suggestionIdx) next = Math.floor(Math.random() * allLines.length);
    setSuggestionIdx(next);
    setTimeout(() => setRolling(false), 150);
  };

  const setFromSuggestion = () => {
    if (!suggestion) return;
    const p = setTodaysIntention({
      text: suggestion.text,
      category: suggestion.category,
      categoryTitle: suggestion.categoryTitle,
    });
    setSelected(p);
    setPulseSeed((s) => s + 1);
    toast.success("Set for today.");
  };

  const reflectInJournal = (text, categoryTitle = "") => {
    // Stash the intention in localStorage; the Journal page reads it once
    // on mount and renders a temporary "Today I'm living with" panel
    // above the composer. We never auto-create a journal entry.
    setCarryingIntention({ text, category: categoryTitle });
    navigate("/journal");
  };

  return (
    <section className="space-y-10 reveal reveal-1" data-testid="daily-intentions">
      {/* Header + primary orb */}
      <header className="grid gap-6 sm:grid-cols-[auto,1fr] items-start">
        <div className="flex justify-center sm:justify-start" data-testid="daily-intentions-orb-wrap">
          <SoulOrb
            size="md"
            mode="aligned"
            pulseSeed={pulseSeed}
            glow={selected ? 0.85 : 0.35}
            ariaLabel="Soul orb — pulses when today's intention is set"
            testid="daily-intentions-orb"
          />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
            <Compass size={12} /> Daily Intentions
          </div>
          <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
            One intention. Set the tone.
          </h2>
          <p
            className="mt-3 max-w-2xl text-[15px] text-[hsl(var(--fg))]/85 leading-relaxed"
            data-testid="daily-intentions-intro"
          >
            Choose one intention for today from the categories below. It will sit at the top of your Home page as a quiet anchor — a single steady thought to carry with you.
          </p>

          {selected && (
            <div
              className="mt-5 inline-flex items-center gap-3 rounded-full border hairline bg-[hsl(var(--sand))]/50 px-4 py-2"
              data-testid="daily-intentions-current"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">Today</span>
              <span
                className="font-display italic text-[15px] text-[hsl(var(--fg))]"
                data-testid="daily-intentions-current-text"
              >
                {selected.text}
              </span>
              <button
                type="button"
                onClick={release}
                aria-label="Release today's intention"
                data-testid="daily-intentions-release"
                className="p-1 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] tab-link"
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ORIENTATION — Choose / Anchor / Act, placed BEFORE the library
          so the member learns the practice before entering the catalogue.
          Three editorial columns on desktop, stacked on mobile. Visual
          language mirrors Daily Affirmations' Choose/Carry/Reflect while
          telling the distinct Intentions story. */}
      <section
        className="pt-2 reveal"
        aria-label="How to use Daily Intentions"
        data-testid="intentions-orientation"
      >
        <div className="grid gap-8 md:gap-6 md:grid-cols-3 md:divide-x md:divide-[hsl(var(--line))]/60">
          {[
            { label: "Choose", body: "Choose what matters today." },
            { label: "Anchor", body: "Return to your intention when the day pulls you away." },
            { label: "Act",    body: "Let one small choice bring the intention to life." },
          ].map((step) => (
            <div
              key={step.label}
              className="md:px-6 text-center md:text-left"
              data-testid={`intentions-step-${step.label.toLowerCase()}`}
            >
              <div className="text-sm font-bold uppercase tracking-[0.3em] text-[hsl(var(--clay))]">
                {step.label}
              </div>
              <p className="mt-3 font-display italic text-[17px] leading-snug text-[hsl(var(--fg))]/90">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Category cards */}
      <div className="grid gap-6 md:grid-cols-2" data-testid="daily-intentions-grid">
        {INTENTION_CATEGORIES.map((cat) => {
          const Icon = ICONS[cat.slug] || Compass;
          return (
            <div
              key={cat.slug}
              className="rounded-md border hairline bg-[hsl(var(--paper))] p-6 md:p-7"
              data-testid={`daily-intentions-card-${cat.slug}`}
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--clay))]">
                <span
                  aria-hidden
                  className="grid place-items-center w-6 h-6 rounded-full bg-[hsl(var(--clay))]/12 text-[hsl(var(--clay))]"
                >
                  <Icon size={12} />
                </span>
                <span data-testid={`daily-intentions-card-title-${cat.slug}`}>{cat.title}</span>
              </div>

              <ul className="mt-5 space-y-3">
                {cat.intentions.map((line, i) => {
                  const isSelected = selected?.text === line;
                  const key = `${cat.slug}-${i}`;
                  const isSpeaking = speakingKey === key;
                  return (
                    <li key={i}>
                      <div
                        className={`group w-full flex items-start gap-3 rounded-md px-3 py-2 -mx-3 transition-colors ${
                          isSelected
                            ? "bg-[hsl(var(--terracotta))]/12"
                            : "hover:bg-[hsl(var(--sand))]/40"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => pick(cat, line)}
                          data-testid={`daily-intentions-line-${cat.slug}-${i}`}
                          className="flex-1 flex items-start gap-3 text-left tab-link min-w-0"
                        >
                          <span
                            aria-hidden
                            className={`mt-1.5 grid place-items-center w-4 h-4 rounded-full border hairline shrink-0 ${
                              isSelected
                                ? "bg-[hsl(var(--terracotta))] border-[hsl(var(--terracotta))] text-[hsl(var(--paper))]"
                                : "text-transparent group-hover:text-[hsl(var(--clay))]/30"
                            }`}
                          >
                            <Check size={10} />
                          </span>
                          <span
                            className={`flex-1 min-w-0 font-display italic text-[15px] leading-snug ${
                              isSelected ? "text-[hsl(var(--fg))]" : "text-[hsl(var(--fg))]/85"
                            }`}
                          >
                            {line}
                          </span>
                        </button>
                        {speechAvailable && (
                          <button
                            type="button"
                            onClick={() => toggleRead(key, line)}
                            aria-label={isSpeaking ? "Stop reading" : "Read aloud"}
                            aria-pressed={isSpeaking}
                            data-testid={`daily-intentions-speak-${cat.slug}-${i}`}
                            className={`shrink-0 mt-1 grid place-items-center w-7 h-7 rounded-full tab-link ${
                              isSpeaking
                                ? "text-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/12"
                                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/10 opacity-60 group-hover:opacity-100"
                            }`}
                          >
                            {isSpeaking ? <VolumeX size={13} /> : <Volume2 size={13} />}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Editorial transition — minimal typographic bridge from the
          intention library to the editorial action image. No card,
          generous whitespace. Mirrors "Choose What Stays With You" on
          Daily Affirmations with intention-specific copy. */}
      <section
        className="text-center max-w-2xl mx-auto pt-2"
        data-testid="intentions-choose-transition"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--terracotta))]">
          Choose What Guides Today
        </div>
        <p className="mt-3 text-[13px] italic text-[hsl(var(--muted-foreground))] leading-relaxed">
          Choose one intention to return to throughout your day — a quiet anchor for how you want to show up.
        </p>
      </section>

      {/* ONE editorial image — the quiet bridge between READING an
          intention and LIVING it. Not another hero; wide and relatively
          shallow with generous whitespace. INTENTION BECOMING ACTION:
          hands pouring tea in soft morning light. */}
      <figure
        className="relative w-full overflow-hidden rounded-md reveal h-[220px] sm:h-[260px] md:h-[320px] lg:h-[380px]"
        data-testid="intentions-action-image"
      >
        <img
          src={EDITORIAL.intentionsAction}
          alt="Hands pouring tea in soft morning light — a small intentional everyday act."
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.95) contrast(0.98)" }}
        />
      </figure>

      {/* YOUR INTENTION MOMENT — the personal practice centerpiece.
          Two states: AN INTENTION FOR TODAY (nothing selected) and
          TODAY'S INTENTION (something selected). */}
      <TodaysIntentionMoment
        selected={selected}
        suggestion={suggestion}
        rolling={rolling}
        onSetSuggestion={setFromSuggestion}
        onPickAnother={rollAnother}
        onRelease={release}
        onReflect={reflectInJournal}
      />

      {/* Closing N8ONE signature — the larger philosophy, understated so
          it does not visually compete with the personal moment above. */}
      <p
        className="text-center text-[13px] italic text-[hsl(var(--muted-foreground))] pt-2"
        data-testid="intentions-closing-signature"
      >
        Move through your life with intention.
      </p>

      {/* Body ritual bridge — incomplete Body rituals to weave in */}
      {incomplete.length > 0 && (
        <section
          className="rounded-md border hairline bg-[hsl(var(--sand))]/30 p-6"
          data-testid="daily-intentions-body-bridge"
        >
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--clay))] inline-flex items-center gap-2">
            <Circle size={11} /> Rituals to weave into today
          </div>
          <p className="mt-2 text-[13px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            Body rituals still waiting on your day. Tap the circle to honor one — no other tab needed.
          </p>
          <ul className="mt-4 divide-y hairline">
            {incomplete.map((r) => (
              <li
                key={r.id}
                className="flex items-center gap-3 py-3"
                data-testid={`daily-intentions-body-ritual-${r.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleRitual(r)}
                  disabled={busy === r.id}
                  aria-label={`Honor ritual ${r.title}`}
                  data-testid={`daily-intentions-body-ritual-toggle-${r.id}`}
                  className="grid place-items-center w-6 h-6 rounded-full border hairline text-transparent hover:text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/10 tab-link disabled:opacity-50"
                >
                  <Check size={12} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[15px] text-[hsl(var(--fg))]">{r.title}</div>
                  {r.time_of_day && (
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                      {r.time_of_day}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

/**
 * TodaysIntentionMoment — the lower centerpiece. Two visual states:
 *
 *   NOT SELECTED → "AN INTENTION FOR TODAY" + rotating line + actions:
 *                  [Set as today's intention] [Another →]
 *   SELECTED     → "TODAY'S INTENTION" + the pinned line + subtitle
 *                  "Return to this when you need it." + reflection prompt +
 *                  REFLECT IN JOURNAL → · Choose another
 *
 * Deliberately quiet — no streaks, points, or celebratory animations.
 * The reward is the practice itself.
 */
function TodaysIntentionMoment({ selected, suggestion, rolling, onSetSuggestion, onPickAnother, onRelease, onReflect }) {
  const text = selected?.text || suggestion?.text || "";
  const category = selected?.category_title || suggestion?.categoryTitle || "";

  if (!text) return null;

  const isSelected = !!selected;

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 via-[hsl(var(--paper))] to-[hsl(var(--paper))] p-7 md:p-10 reveal"
      data-testid={isSelected ? "intentions-todays-intention" : "intentions-for-today"}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-16 w-64 h-64 rounded-full opacity-[0.08]"
        style={{ background: "radial-gradient(circle, hsl(var(--terracotta)) 0, transparent 65%)" }}
      />
      <div className="relative max-w-3xl">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--terracotta))]">
          <Compass size={11} />
          <span data-testid={isSelected ? "intentions-todays-eyebrow" : "intentions-for-today-eyebrow"}>
            {isSelected ? "Today's intention" : "An intention for today"}
          </span>
          {category && (
            <span className="normal-case tracking-normal text-[hsl(var(--muted-foreground))]">
              · {category}
            </span>
          )}
        </div>

        <p
          className="mt-4 font-display italic text-2xl md:text-3xl leading-snug text-[hsl(var(--fg))]"
          data-testid={isSelected ? "intentions-todays-text" : "intentions-for-today-text"}
        >
          “{text}”
        </p>

        {isSelected && (
          <p className="mt-3 text-[13px] italic text-[hsl(var(--muted-foreground))]">
            Return to this when you need it.
          </p>
        )}

        {/* Primary actions */}
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          {isSelected ? (
            <>
              <button
                type="button"
                onClick={() => onReflect(text, category)}
                data-testid="intentions-moment-reflect"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] tab-link"
              >
                <Feather size={13} /> Reflect in Journal
              </button>
              <button
                type="button"
                onClick={onRelease}
                data-testid="intentions-moment-choose-another"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
              >
                Choose another
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onSetSuggestion}
                data-testid="intentions-moment-set"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--clay))] hover:text-[hsl(var(--terracotta))] tab-link"
              >
                <Check size={13} /> Set as today&apos;s intention
              </button>
              <button
                type="button"
                onClick={onPickAnother}
                disabled={rolling}
                data-testid="intentions-moment-another"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
              >
                Another →
              </button>
            </>
          )}
        </div>

        {/* Optional reflective prompt — only shown once an intention is
            chosen. Does NOT require an answer; the Journal link uses the
            existing Carry-into-Journal pattern via setCarryingIntention. */}
        {isSelected && (
          <p
            className="mt-6 text-[12px] italic text-[hsl(var(--muted-foreground))] leading-relaxed"
            data-testid="intentions-moment-reflective-prompt"
          >
            What would living this intention look like today?
          </p>
        )}
      </div>
    </section>
  );
}
